import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/mockData';
import axios from 'axios';

const BACKEND_URL = "https://loscabosgr8.com";

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      });

      if (!response.data.success) {
        throw new Error("Contact form submission failed");
      }

      
      toast({
        title: language === 'en' ? "Quote Request Sent!" : "¡Solicitud Enviada!",
        description: language === 'en' 
          ? "We'll get back to you within 24 hours. Check your email for confirmation." 
          : "Te responderemos en 24 horas. Revisa tu email para confirmación.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: language === 'en' ? "Error" : "Error",
        description: language === 'en' 
          ? "Failed to send request. Please try again or contact us directly." 
          : "Error al enviar solicitud. Por favor intenta de nuevo o contáctanos directamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = [
    language === 'en' ? 'Interior & Exterior Painting' : 'Pintura Interior y Exterior',
    language === 'en' ? 'Carpentry' : 'Carpintería',
    language === 'en' ? 'Remodeling' : 'Remodelación',
    language === 'en' ? 'Maintenance Services' : 'Servicios de Mantenimiento',
    language === 'en' ? 'Other' : 'Otro'
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Arial, sans-serif' }}>
              {t.contact.title}
            </h2>
            <p className="text-xl text-slate-600 mb-6">
              {t.contact.subtitle}
            </p>
            <div className="w-24 h-1 bg-amber-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-100">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-semibold">
                    {t.contact.form.name}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 border-slate-300 focus:border-amber-600 focus:ring-amber-600"
                    placeholder={language === 'en' ? 'John Doe' : 'Juan Pérez'}
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-semibold">
                    {t.contact.form.email}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 border-slate-300 focus:border-amber-600 focus:ring-amber-600"
                    placeholder={language === 'en' ? 'john@example.com' : 'juan@ejemplo.com'}
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-slate-700 font-semibold">
                    {t.contact.form.phone}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 border-slate-300 focus:border-amber-600 focus:ring-amber-600"
                    placeholder="+52 624 123 4567"
                  />
                </div>

                {/* Service */}
                <div>
                  <Label htmlFor="service" className="text-slate-700 font-semibold">
                    {t.contact.form.service}
                  </Label>
                  <Select value={formData.service} onValueChange={handleServiceChange}>
                    <SelectTrigger className="mt-2 border-slate-300 focus:border-amber-600 focus:ring-amber-600">
                      <SelectValue placeholder={t.contact.form.selectService} />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((service, index) => (
                        <SelectItem key={index} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-slate-700 font-semibold">
                    {t.contact.form.message}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="mt-2 border-slate-300 focus:border-amber-600 focus:ring-amber-600 resize-none"
                    placeholder={language === 'en' 
                      ? 'Tell us about your project...' 
                      : 'Cuéntanos sobre tu proyecto...'}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {language === 'en' ? 'Sending...' : 'Enviando...'}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      {t.contact.form.submit}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              {/* Contact Cards */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl shadow-xl text-white">
                <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Arial, sans-serif' }}>
                  {t.contact.info.title}
                </h3>

                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-amber-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Email</p>
                      <a href="mailto:services@loscabosgr8.com" className="text-amber-400 hover:text-amber-300 transition-colors break-all">
                        services@loscabosgr8.com
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">{t.contact.info.phone}</p>
                      <a href="tel:+526243001035" className="text-blue-400 hover:text-blue-300 transition-colors">
                        +52 624 300 1035
                      </a>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-green-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">{t.contact.info.whatsapp}</p>
                      <a 
                        href="https://wa.me/5216243001035" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300 transition-colors"
                      >
                        +52 624 300 1035
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start space-x-4">
                    <div className="bg-slate-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">{t.contact.info.location}</p>
                      <p className="text-slate-300">
                        {t.contact.info.locationValue}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-64">
                <img 
                  src="https://images.unsplash.com/photo-1521791055366-0d553872125f?w=800&q=80"
                  alt="Contact us"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-6">
                  <p className="text-white text-lg font-semibold">
                    {language === 'en' 
                      ? "Ready to start your project? Let's talk!" 
                      : "¿Listo para comenzar tu proyecto? ¡Hablemos!"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
